/**
 * EditSalaryModalV2 - Edit employee salary modal with Tailwind/shadcn UI.
 * Replaces ChakraUi/Modals/EditSalaryModal.tsx
 */
import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import updateUserSalary from '../../api/users/updateUserSalary';
import { formatArcaNumber } from '../../utils/formatArcaNumbers';

export interface EditSalaryUser {
	id: string;
	email: string;
	grossAnnualSalary: number;
}

interface EditSalaryModalV2Props {
	isOpen: boolean;
	onClose: () => void;
	user: EditSalaryUser;
	token: string;
	onSuccess: () => void;
	translate: (key: string) => string;
}

const EditSalaryModalV2: React.FC<EditSalaryModalV2Props> = ({
	isOpen,
	onClose,
	user,
	token,
	onSuccess,
	translate
}) => {
	const [salaryInput, setSalaryInput] = useState<string>(String(user.grossAnnualSalary));
	const [isLoading, setIsLoading] = useState(false);
	const { toast } = useToast();

	// Sync state when user changes
	useEffect(() => {
		setSalaryInput(String(user.grossAnnualSalary));
	}, [user.grossAnnualSalary]);

	// Convert input string to number (supports both comma and dot as decimal separator)
	const parseInputToNumber = (input: string): number => {
		const normalized = input.replace(',', '.');
		const parsed = parseFloat(normalized);
		return isNaN(parsed) ? 0 : parsed;
	};

	const newSalary = parseInputToNumber(salaryInput);
	const hasChanged = newSalary !== user.grossAnnualSalary;
	const isValidNumber = salaryInput !== '' && !isNaN(parseInputToNumber(salaryInput));

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		// Allow digits, one dot or comma, and up to 2 decimal places
		if (value === '' || /^[0-9]*[.,]?[0-9]{0,2}$/.test(value)) {
			setSalaryInput(value);
		}
	};

	const handleSubmit = async () => {
		if (!hasChanged || !isValidNumber) return;

		setIsLoading(true);
		try {
			const response = await updateUserSalary(token, user.id, newSalary);
			if (response.error) {
				throw new Error(response.error);
			}
			toast({
				title: translate('admin.salary.success'),
				variant: 'default'
			});
			onSuccess();
		} catch (error) {
			toast({
				title: translate('admin.salary.error'),
				variant: 'destructive'
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setSalaryInput(String(user.grossAnnualSalary));
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{translate('admin.salary.editTitle')}</DialogTitle>
					<DialogDescription>
						{translate('admin.salary.editMessage')} <strong>{user.email}</strong>{' '}
						{translate('admin.salary.from')} {formatArcaNumber(user.grossAnnualSalary)}€{' '}
						{translate('admin.salary.to')} {formatArcaNumber(newSalary)}€
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="salary">{translate('admin.salary.newSalary')}</Label>
						<Input
							id="salary"
							value={salaryInput}
							onChange={handleInputChange}
							placeholder="0.00"
							inputMode="decimal"
						/>
					</div>
				</div>

				<DialogFooter className="gap-2">
					<Button variant="outline" onClick={handleClose}>
						{translate('admin.salary.cancel')}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!hasChanged || !isValidNumber || isLoading}
						className="bg-primary hover:bg-primary/90"
					>
						{isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
						{translate('admin.salary.confirm')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EditSalaryModalV2;
